import React, { useState, useRef, useEffect } from 'react';
import styles from './VoucherList.module.scss';
import { useReserveVoucher, useReleaseVoucher } from '~/shared/hooks/loyalty';
import { Voucher } from '~/lib/data-contract/loyalty';
import { useBasket } from '~/features/basket';
import { useErrorHandler } from '~/shared/utils/errorBoundary/hooks/useErrorHandler';
import { useTranslation } from '~/shared/utils/translation';
import { useMutationState } from '@tanstack/react-query';
import { MutationKeys } from '~/services/loyalty/constants';
import { VoucherItem } from '../VoucherItem';
import { CTA } from '~/shared/components/ProductCard/components/CTA';

type A11yData = {
    index: number;
    voucherCount: number;
};

export const VoucherList = ({
    vouchers = [],
}: {
    vouchers?: Pick<Voucher, 'expirationDate' | 'barcode' | 'description'>[];
}) => {
    const [showAll, setShowAll] = useState(false);
    const extraCardRef = useRef<HTMLDivElement | null>(null);

    const basket = useBasket();
    const { mutate: reserveVoucher } = useReserveVoucher();
    const { mutate: releaseVoucher } = useReleaseVoucher();
    const { trackAndDisplayError: trackError } = useErrorHandler();

    const { translate } = useTranslation();

    const pendingVoucherBarcodes = useMutationState({
        filters: { mutationKey: [MutationKeys.RESERVE_VOUCHER], status: 'pending' },
        select: (mutation) => mutation.state.variables,
    });

    const pendingVoucherCancellations = useMutationState({
        filters: { mutationKey: [MutationKeys.RELEASE_VOUCHER], status: 'pending' },
        select: (mutation) => mutation.state.variables,
    });

    const getCouponItemIdHandler = (barcode: string) =>
        basket.couponItems?.find((x) => x.code === barcode)?.couponItemId;

    const isVoucherActiveHandler = (barcode: string) =>
        basket.couponItems?.some((x) => x.code === barcode) ?? false;

    const releaseVoucherHandler = (barcode: string) => {
        const couponItemId = getCouponItemIdHandler(barcode);
        if (!couponItemId) return trackError(translate('basket.couldNotFindCoupon'));
        releaseVoucher(couponItemId);
    };

    const visibleVouchers = showAll ? vouchers : vouchers.slice(0, 2);

    useEffect(() => {
        if (showAll && extraCardRef.current) {
            extraCardRef.current.focus();
        }
    }, [showAll]);

    return (
        <div className={styles.voucherListWrapper}>
            <div className={styles.titleInfoWrapper}>
                <h4 className={styles.title}>{translate('basket.vouchersTitle')}</h4>
                <p className={styles.info} role="note">
                    {translate('basket.vouchersInfo')}
                </p>
            </div>

            <div id="vouchers-list">
                {visibleVouchers.map(({ expirationDate, barcode, description }, index) => {
                    const isFirstExtra = showAll && index === 2 && vouchers.length > 2;

                    const active = isVoucherActiveHandler(barcode);
                    const isPendingReserve = pendingVoucherBarcodes.includes(barcode);
                    const isPendingCancel = pendingVoucherCancellations.includes(
                        getCouponItemIdHandler(barcode),
                    );

                    const a11yData: A11yData = {
                        index,
                        voucherCount: vouchers.length,
                    };

                    return (
                        <VoucherItem
                            description={description}
                            a11yData={a11yData}
                            key={barcode}
                            ref={isFirstExtra ? extraCardRef : null}
                            barcode={barcode}
                            expirationDate={expirationDate}
                            isVoucherActive={active}
                            isPendingReserve={isPendingReserve}
                            isPendingCancel={isPendingCancel}
                            onRedeem={() => reserveVoucher(barcode)}
                            onCancel={() => releaseVoucherHandler(barcode)}
                            isFirstExtra={isFirstExtra}
                        />
                    );
                })}
            </div>

            {vouchers.length > 2 && (
                <CTA
                    variant="plain"
                    text={showAll ? 'Vis færre' : `Vis alle (${vouchers?.length})`}
                    onClick={() => setShowAll((prev) => !prev)}
                    aria-expanded={showAll}
                    aria-controls="vouchers-list"
                />
            )}
        </div>
    );
};
